import { useIntl } from 'react-intl';
import Modal from '../../../../../../../components/Modal';
import PropTypes from 'prop-types';
import styles from './index.module.scss';
import { Divider, Flex, Form } from 'antd';
import { useContext, useEffect, useState } from 'react';
import AccionaButton from '../../../../../../../components/AccionaButton';
import { useDispatch, useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import { NotificationsContext } from '../../../../../../../context/NotificationsContext';
import { MainContext } from '../../../../../../../context/MainContext';
import { getConcreteBatches } from '../../../../../../../services/redux/concreteBatch/actions';
import BatchDescriptions from './components/BatchDescriptions';
import BatchForm from './components/BatchForm';
import useLotification from './hooks/useLotification';
import useNormativeApi from './hooks/useNormativeApi';
import { updateDeliveryNote } from '../../../../../../../services/redux/deliveryNotes/actions';

const { wrapper } = styles;

const EditModal = ({
  item,
  onClose,
  onCancel,
  normative,
  structuralElements,
  itemsToEditOnBulk,
}) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { setLoading } = useContext(MainContext);
  const { pushNotification } = useContext(NotificationsContext);
  const { project } = useOutletContext();
  const { list: concreteBatches, fetching } = useSelector(
    (state) => state.concreteBatch,
  );
  const [currentFormValues, setCurrentFormValues] = useState(
    item?.rawData || {},
  );

  const { getProjectStatus } = useNormativeApi();

  const { unBatchItem, createNewConcreteBatch, changeToAnExistingBatch } =
    useLotification();

  const updateItem = ({ deliveryNote, formValues }, isLotification) => {
    setLoading(true);
    if (!formValues.batch_code) {
      dispatch(
        updateDeliveryNote({
          deliveryNote: { ...deliveryNote, ...formValues },
          callback: async () => {
            setLoading(false);
            onClose();
            pushNotification({
              message: intl.formatMessage({
                id: 'pages.quality.normative.traceability.edit.noBatch.notification.success',
              }),
              type: 'success',
            });
          },
        }),
      );
    } else if (formValues.batch_code === 'unbatch') {
      unBatchItem(deliveryNote, formValues, isLotification)
        .then(() => {
          setLoading(false);
          onClose();
          pushNotification({
            message: intl.formatMessage({
              id: 'pages.quality.normative.traceability.edit.unbatch.notification.success',
            }),
            type: 'success',
          });
        })
        .catch((error) => {
          setLoading(false);
          onClose();
          pushNotification({
            message: intl.formatMessage({
              id: 'pages.quality.normative.traceability.edit.unbatch.notification.error',
            }),
            type: 'error',
          });
        });
    } else if (formValues.batch_code === 'newBatch') {
      createNewConcreteBatch(deliveryNote, formValues, isLotification)
        .then(() => {
          setLoading(false);
          onClose();
          pushNotification({
            message: intl.formatMessage({
              id: 'pages.quality.normative.traceability.edit.newBatch.notification.success',
            }),
            type: 'success',
          });
        })
        .catch((error) => {
          console.error(error);
          setLoading(false);
          onClose();
          pushNotification({
            message: intl.formatMessage({
              id: 'pages.quality.normative.traceability.edit.newBatch.notification.error',
            }),
            type: 'error',
          });
        });
    } else {
      changeToAnExistingBatch(deliveryNote, formValues, isLotification)
        .then(() => {
          setLoading(false);
          onClose();
          pushNotification({
            message: intl.formatMessage({
              id: 'pages.quality.normative.traceability.edit.changeBatch.notification.success',
            }),
            type: 'success',
          });
        })
        .catch((error) => {
          console.error(error);
          setLoading(false);
          onClose();
          pushNotification({
            message: intl.formatMessage({
              id: 'pages.quality.normative.traceability.edit.changeBatch.notification.error',
            }),
            type: 'error',
          });
        });
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      ...item?.rawData,
      batch_code: item?.rawData.concrete_batch,
      concrete_quality: item?.rawData.concrete_quality || false,
      certified_dis: item?.rawData.certified_dis || false,
      delta: item?.rawData.delta || null,
      tag: item?.concrete_batch_data?.tag_batch_data?.id_tag,
    });
  }, [form, item, item?.rawData]);

  useEffect(() => {
    setCurrentFormValues((prev) => ({
      ...prev,
      ...item?.rawData,
      tag: item?.concrete_batch_data?.tag_batch_data?.id_tag,
    }));
  }, [item, item?.rawData]);

  useEffect(() => {
    const filterKeys = [
      ...(normative === 'coe' ? ['certified_dis'] : []),
      'structural_element',
      'control_type',
      'recipe',
      'plant',
      'concrete_quality',
      ...(project.version === 2 ? ['tag'] : []),
    ];
    const filters = {};
    filterKeys.forEach((key) => {
      let value = currentFormValues[key] ?? '';
      if (key === 'recipe' && value) value = value.replace('+', '%2B');
      if (key === 'tag') {
        if (value) {
          filters['tag_batch_data[id_tag]'] =
            value === 'newTag' ? undefined : value;
        }
      } else {
        filters[`delivery_note_data[${key}]`] =
          key === 'concrete_quality' || key === 'certified_dis'
            ? value || false
            : value;
      }
    });

    if (
      !itemsToEditOnBulk &&
      Object.values(filters).every((value) => value !== undefined && item)
    ) {
      dispatch(
        getConcreteBatches({
          project: project.id_project,
          filters,
          size: 1000,
        }),
      );
    }
  }, [
    currentFormValues,
    dispatch,
    item,
    normative,
    project.id_project,
    itemsToEditOnBulk,
    project,
  ]);

  useEffect(() => {
    if (!item) {
      form.resetFields();
    }
  }, [form, item]);

  return (
    <Modal
      open={!!item}
      title={intl
        .formatMessage({
          id: 'pages.quality.normative.traceability.edit.title',
        })
        .toUpperCase()}
      closable
      width="60vw"
      onClose={onClose}
      onCancel={onCancel}
    >
      <div className={wrapper}>
        <BatchDescriptions item={item} />
        <Divider />
        <BatchForm
          disableBatchSelector={!!itemsToEditOnBulk}
          fetchingBatches={fetching}
          form={form}
          onValuesChange={(values) => {
            setCurrentFormValues((prev) => ({ ...prev, ...values }));
          }}
          structuralElements={structuralElements}
          allowUnbatch={Boolean(item?.rawData?.concrete_batch !== null)}
          normative={normative}
          deliveryNote={item?.rawData}
          availableBatches={concreteBatches}
        />
        <Flex justify="space-between" style={{ marginTop: '5em' }}>
          <AccionaButton
            type="default"
            onClick={onCancel}
            msg={intl.formatMessage({ id: 'cancel' })}
          />
          <AccionaButton
            type="primary"
            onClick={() => {
              form.validateFields().then((values) => {
                // Si control_type es 100%, los campos certified_dis y delta no están en el DOM
                // y validateFields no los incluye, por lo que hay que forzarlos explícitamente
                const formValues = { ...values };
                if (values.control_type === '100%') {
                  formValues.certified_dis = false;
                  formValues.delta = null;
                }
                
                const payload = {
                  deliveryNote: item?.rawData,
                  formValues,
                };
                if (!itemsToEditOnBulk) {
                  getProjectStatus(payload, true)
                    .then(updateItem(payload, true))
                    .catch();
                } else {
                  itemsToEditOnBulk.forEach((itemToEdit) => {
                    const itemPayload = {
                      deliveryNote: itemToEdit.rawData,
                      formValues: {
                        ...formValues,
                        concrete_batch: null,
                        batch_code: null,
                      },
                    };
                    getProjectStatus(itemPayload, true)
                      .then(() => updateItem(itemPayload, true))
                      .catch();
                  });
                }
              });
            }}
            msg={intl.formatMessage({
              id: 'pages.quality.normative.traceability.edit.config.button',
            })}
          />
        </Flex>
      </div>
    </Modal>
  );
};

EditModal.propTypes = {
  normative: PropTypes.oneOf(['coe', 'ehe']).isRequired,
  item: PropTypes.shape({
    docket_number: PropTypes.element,
    plant: PropTypes.string,
    recipe: PropTypes.string,
    delivered: PropTypes.element,
    elements: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    arriving_date: PropTypes.string,
    detailed_location: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.element,
    ]),
    structural_element: PropTypes.string,
    control_type: PropTypes.string,
    concreteBatch: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    batchCompliance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    concrete_batch_data: PropTypes.shape({
      tag_batch_data: PropTypes.shape({
        id_tag: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    }),
    laboratoryNoteData: PropTypes.shape({
      reference_number: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      compressive_strength_28_1: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      compressive_strength_28_2: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      compressive_strength_28_3: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      compressive_strength_3_1: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      compressive_strength_7_1: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      compressive_strength_60_1: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    }),
    specimens_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rawData: PropTypes.any,
  }),
  onClose: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  structuralElements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      structural_element: PropTypes.string,
    }),
  ),
  itemsToEditOnBulk: PropTypes.arrayOf(
    PropTypes.shape({
      docket_number: PropTypes.element,
      plant: PropTypes.string,
      recipe: PropTypes.string,
      delivered: PropTypes.element,
      elements: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      arriving_date: PropTypes.string,
      detailed_location: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element,
      ]),
      structural_element: PropTypes.string,
      control_type: PropTypes.string,
      concreteBatch: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      batchCompliance: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      laboratoryNoteData: PropTypes.shape({
        reference_number: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        compressive_strength_28_1: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        compressive_strength_28_2: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        compressive_strength_28_3: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        compressive_strength_3_1: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        compressive_strength_7_1: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        compressive_strength_60_1: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
      }),
      specimens_number: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      rawData: PropTypes.any,
    }),
  ),
};

EditModal.defaultProps = {
  item: null,
  structuralElements: [],
  itemsToEditOnBulk: undefined,
};

export default EditModal;
